import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { PaymentValidators } from '../../../../validators/payment-validators/payment-validators';
import { PaymentDateMaskDirective } from '../../../../directives/payment-directives/date-mask-directive.directive';
import { PaymentCardNumberMaskDirective } from '../../../../directives/payment-directives/payment-card-number-mask.directive';
import { DigitOnlyDirective } from '../../../../directives/payment-directives/digit-only.directive';
import { CVV_HINT_CONFIG, PAYMENT_ERROR_MESSAGES } from '../../../../constants';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { CvvHintComponentComponent } from '../../card-hint-component/cvv-hint-component/cvv-hint-component.component';
import { CardService } from '../../../../services/payment-card/card.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, tap } from 'rxjs';
import { SnackbarService } from '@app/services/snackbar/snackbar.service';
import { SnackbarStatus } from '@app/utils/enums/snackbar.enum';
import { TranslatePipe } from '@ngx-translate/core';
import { SubscribeStatesService } from '../../../../services/subscribe-state/subscribe-states.service';

@Component({
  selector: 'app-add-payment-content',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    IconComponent,
    PaymentDateMaskDirective,
    PaymentCardNumberMaskDirective,
    DigitOnlyDirective,
    MatButtonModule,
    MatMenuModule,
    CvvHintComponentComponent,
    TranslatePipe,
  ],
  templateUrl: './add-payment-content.component.html',
  styleUrl: './add-payment-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddPaymentContentComponent {
  private fb = inject(FormBuilder);
  private cardService = inject(CardService);
  private destroyRef = inject(DestroyRef);
  private snackbarService = inject(SnackbarService);
  private subscribeState = inject(SubscribeStatesService);
  public readonly iconName = IconName;
  public readonly closeListener = output<void>();

  private readonly ERROR_MESSAGES = PAYMENT_ERROR_MESSAGES;
  protected readonly hintMenuConfig = CVV_HINT_CONFIG;

  public isSubmitting = signal<boolean>(false);

  public paymentForm = this.fb.group({
    cardNumber: ['', [Validators.required, PaymentValidators.validCardNumber()]],
    cardHolder: ['', [Validators.required, Validators.maxLength(50), PaymentValidators.nameOnly()]],
    expireDate: ['', [Validators.required, Validators.maxLength(5), PaymentValidators.validDate()]],
    ccvData: ['', [Validators.required, PaymentValidators.validCVV()]],
  });

  @ViewChild('hintTrigger') menuTrigger!: MatMenuTrigger;

  public onSubmit(cardType: string): void {
    this.isSubmitting.set(true);
    const form = this.paymentForm;

    if (form.invalid) {
      form.markAllAsTouched();
      this.isSubmitting.set(false);
      return;
    }
    this.onMethodAdd(cardType);
  }

  public onCancel(): void {
    this.closeListener.emit();
  }

  public getErrorMessage(controlName: string): string | null {
    const control = this.paymentForm.get(controlName);

    if (!control || !control.touched || !control.errors) {
      return '';
    }
    const errors = control.errors;

    if (errors['required']) {
      return this.ERROR_MESSAGES['required'];
    }

    const firstErrorKey = Object.keys(errors)[0];
    return this.ERROR_MESSAGES[firstErrorKey];
  }

  public onMethodAdd(cardType: string): void {
    this.cardService
      .addPaymentMethod(cardType)
      .pipe(
        tap(() => {
          this.isSubmitting.set(false);
          this.onCancel();
          this.snackbarService.openSnackbar(
            SnackbarStatus.SUCCESS,
            'shop-settings.subscription.paymentMethod.addPaymentMethodModal.snackbar.success',
          );
          this.subscribeState.notifyCardsUpdate();
        }),
        catchError(() => {
          this.isSubmitting.set(false);
          this.onCancel();
          this.snackbarService.openSnackbar(
            SnackbarStatus.FAIL,
            'shop-settings.subscription.paymentMethod.addPaymentMethodModal.snackbar.fail',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
